import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  NO_ERRORS_SCHEMA,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { Swiper } from 'swiper';
import { SwiperOptions } from 'swiper/types';
import { HTTPSearchResultTour } from 'src/app/models';
import { ApiService } from 'src/app/services/api.service';
import { pluralize } from '../../pipes/pluralize/pluralize';
import {HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig} from "../../../config/hotel-tours-list-page.config";
import { TourRecommendationsComponent } from '../tour-recommendations/tour-recommendations.component';

@Component({
  selector: 'app-tour-image',
  templateUrl: './tour-image.component.html',
  styleUrls: ['./tour-image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  schemas: [NO_ERRORS_SCHEMA],
    imports: [CommonModule, TourRecommendationsComponent]
})
export class TourImageComponent implements OnInit, OnDestroy {
  @ViewChild('swiperGallery', { static: false }) swiperGallery?: ElementRef<HTMLElement>;
  @Input() tour: HTTPSearchResultTour;

  isShowDiscount = true;
  isPhotosLoaded = false;
  isPhotoLoading = false;
  imageCollection: string[] = [];
  currentPhotoIndex = 1;

  readonly swiperConfig: SwiperOptions = {
    observer: false,
    direction: 'horizontal',
    spaceBetween: 0,
    slidesPerView: 1,
    centeredSlides: true,
    pagination: false,
    navigation: false,
  };

  private swiperInstance: Swiper | null = null;

  constructor(
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private appConfig: HotelToursPageConfig,
    private apiService: ApiService,
    private cdRef: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.isShowDiscount = this.appConfig.isShowDiscount && this.tour?.discount > 0;
  }

  ngOnDestroy(): void {
    this.swiperInstance?.destroy(true, true);
    this.swiperInstance = null;
  }

  get mainPhotoSrc() {
    if (this.tour.photo) {
      return `${this.appConfig.photoBasePath}/${this.tour.photo}`;
    }

    return this.appConfig.noPhotoLink;
  }

  get salesCount() {
    return pluralize(this.tour.salesCount, ['раз', 'раза', 'раз']);
  }

  get isSliderAvailable(): boolean {
    return this.tour.photosCount > 1;
  }

  get sliderBullets(): number[] {
    const bulletsCount = Math.max(this.tour.photosCount, 1);

    return Array.from({ length: bulletsCount }, (_, index) => index + 1);
  }

  get activeBulletIndex(): number {
    return this.currentPhotoIndex;
  }

  get isPrevArrowVisible(): boolean {
    return this.isPhotosLoaded && this.currentPhotoIndex > 1;
  }

  get bulletTrackTransform(): string {
    const offset = Math.max(this.currentPhotoIndex - 3, 0) * 11;

    return `translateX(-${offset}px)`;
  }

  public isSmallBullet(bullet: number): boolean {
    if (this.tour.photosCount <= 3) {
      return false;
    }

    const smallBulletIndex = Math.min(Math.max(this.currentPhotoIndex + 1, 4), this.tour.photosCount);

    return bullet === smallBulletIndex;
  }

  public onPrevPhoto(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.prevPhoto();
  }

  public onNextPhoto(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.nextPhoto();
  }

  private prevPhoto(): void {
    if (this.isPhotosLoaded) {
      this.swiperInstance?.slidePrev();
      return;
    }

    this.loadPhoto();
  }

  private nextPhoto(): void {
    if (this.isPhotosLoaded) {
      this.swiperInstance?.slideNext();
      return;
    }

    this.loadPhoto();
  }

  private loadPhoto(): void {
    if (this.isPhotosLoaded || this.isPhotoLoading) {
      return;
    }

    this.isPhotoLoading = true;
    this.cdRef.markForCheck();

    this.apiService.loadHotelPhoto(this.tour.hotelId).subscribe((photos: string[]) => {
      this.imageCollection = this.normalizeLoadedPhotos(photos);
      this.isPhotosLoaded = true;
      this.isPhotoLoading = false;
      this.cdRef.detectChanges();

      setTimeout(() => {
        this.initSwiper();
      }, 0);
    });
  }

  private initSwiper(): void {
    if (!this.swiperGallery) {
      return;
    }

    this.swiperInstance?.destroy(true, true);
    this.swiperInstance = new Swiper(this.swiperGallery.nativeElement, {
      ...this.swiperConfig,
      on: {
        init: () => this.animateToSecondSlide(),
        slideChange: (swiper) => {
          this.currentPhotoIndex = swiper.realIndex + 1;
          this.cdRef.markForCheck();
        },
      },
    });
  }

  private animateToSecondSlide(): void {
    this.currentPhotoIndex = 1;
    this.cdRef.markForCheck();

    if (this.imageCollection.length < 2) {
      return;
    }

    setTimeout(() => {
      this.swiperInstance?.slideTo(1, 300);
    }, 0);
  }

  private normalizeLoadedPhotos(photos: string[]): string[] {
    if (!photos.length) {
      return [this.mainPhotoSrc];
    }

    return photos;
  }
}
